// MobileShell — the MOBILE chat-shell renderer (platform-isolation Phase 1, #1657).
// FORK of the former App.tsx trailing chat JSX (old lines 2147-3388), VERBATIM.
// Pure renderer over useChatShell()'s returned state — owns NO state itself.
// FIREWALL: lives in mobile/ — may import shared-ui/ state/ hooks/ api/ shared/ +
// components/, but NOT desktop/.
//
// Design-token slice (#2196, lane va-ui-design-tokens-2026-10-01): the shell root
// stamps the unified semantic token family (`--va-*`, dark-first) via
// applyDesignTokens — the shell chrome bands (top bar / composer / bottom nav)
// consume it in style.css. Same source as the desktop shell
// (frontend/src/design-tokens.ts), pinned against drift by
// setpoint-evals/ui-design-tokens/. The legacy theme switcher keeps owning the
// chat CONTENT surfaces (zero product delta); per-component adoption is follow-up.

import { InputBar } from '../components/InputBar';
import { applyDesignTokens } from '../design-tokens';
import { ChatArea } from '../components/ChatArea';
import { PlaybackFab } from '../components/PlaybackFab';
import { CommandPalette } from '../components/CommandPalette';
import { ConsoleView } from '../shared-ui/ConsoleView';
import { UnifiedDrawer } from '../components/UnifiedDrawer';
import { VoicePicker } from '../components/VoicePicker';
import { SshTerminal } from '../components/SshTerminal';
import { SshTargetPicker } from '../components/SshTargetPicker';
import { SshPttButton } from '../components/SshPttButton';
import { SshVoiceHistory } from '../components/SshVoiceHistory';
import { SshToolbar } from '../components/SshToolbar';
import { SshLinkBar } from '../components/SshLinkBar';
import { blobToBase64 } from '../utils/blobToBase64';
import { ContextBar } from '../components/ContextBar';
import { GearMenu } from '../components/GearMenu';
import { DrawerHost } from '../components/DrawerHost';
import { GrafanaTasksPanel } from '../components/GrafanaTasksPanel';
import { closeDrawer } from '../state/drawerStore';
import { useContextFullnessStore } from '../state/contextFullnessStore';
import { DevPreviewButton } from '../components/DevPreviewButton';
import { WorkflowStatusIcon } from '../components/WorkflowStatusIcon';
import { AgentRunsPanel } from '../components/AgentRunsPanel';
import { BottomNav } from '../components/BottomNav';
import { FloatingActionCluster } from '../components/FloatingActionCluster';
import { BottomComposer } from '../components/BottomComposer';
import { DocTtsPlaybackBar } from '../components/DocTtsPlaybackBar';
import { useChatTtsSlot, useDocTtsStore } from '../state/docTtsStore';
import { SettingsModal } from '../components/SettingsModal';
import { SettingsStoreDevProbe } from '../components/SettingsStoreDevProbe';
import { useSettingsStore } from '../state/settings.store';
import { useAgentListStore, pickSleepAdvanceTarget } from '../state/agentListStore';
import { isAgentSource } from '../state/sessionListStore';
import { SessionDot } from '../components/SessionDot';
import { ConsentModal } from '../components/ConsentModal';
import { GeoGate } from '../components/GeoGate';
import { BrowserConsentModal } from '../components/BrowserConsentModal';
import { BrowserTierGateModal } from '../components/BrowserTierGateModal';
import { OnboardingWizard } from '../components/OnboardingWizard';
import { CodeViewer } from '../components/CodeViewer';
import { FileListDrawer } from '../components/FileListDrawer';
import { ShareTargetModal } from '../components/ShareTargetModal';
import { ModelControlSheet } from '../components/ModelControlSheet';
import { HighCostEffortModal } from '../components/HighCostEffortModal';
import { SessionListView } from '../components/SessionListView';
import { UsageTab } from '../components/UsageTab';
import { formatDeployPending } from '../utils/deployStatusLabel';
import { getBrand, getBrandId } from '../brands';
import { WsEvent, PREDEFINED_ROLES } from '@voice-assistant/shared';
import { LogbookModal } from '../components/LogbookModal';
import { ProjectChips } from '../shared-ui/ProjectChips';
import { PlanChip } from '../shared-ui/PlanChip';
import { QuotePill } from '../shared-ui/QuotePill';
import { useComposerStore } from '../state/composerStore';
import { ExplainTargetModal } from '../components/ExplainTargetModal';
import { intentFriendlyLabel } from '../utils/intentLabel';
import { modelShortName } from '../utils/modelHelpers';
import { ReaderToggle } from '../components/ReaderMode/ReaderToggle';
import { ImageLightbox } from '../components/ImageLightbox';
import { CoachLayer } from '../shared-ui/CoachLayer';
import { show as coachShow } from '../state/coachStore';
import { BottomFindBar } from '../components/BottomFindBar';
import { useChatShell } from '../hooks/useChatShell';

/**
 * Design-token stamp (#2196) — module-level stable callback: applies the unified
 * dark family (`--va-*`) to the shell root BEFORE paint (ref callbacks run at mount,
 * ahead of the first painted frame). `data-va-variant="dark"` on the root below is
 * the same contract the SEs read.
 */
const stampDesignTokens = (el: HTMLDivElement | null) => {
  if (el) applyDesignTokens(el);
};

export function MobileShell(props: ReturnType<typeof useChatShell>) {
  const {
    CONSULT_PROVIDERS,
    activeComposeExpanded,
    activeRole,
    activeRoleRef,
    activeView,
    attachError,
    attachProcessing,
    attachments,
    authenticated,
    autoSubmitVoice,
    billingEnabled,
    browserConsentPrompt,
    browserPanelEnabled,
    browserTierGatePrompt,
    busy,
    cancelPrompt,
    chatFind,
    chatSlideRef,
    chatTab,
    chatTtsAllowed,
    checkedProjects,
    clearAttachments,
    clearLogbookState,
    clearProviderError,
    closeDrawerH,
    closeFiles,
    closeLogbook,
    closeSettings,
    confirmDelete,
    confirmReset,
    connected,
    consentNeeded,
    consoleView,
    consultTarget,
    contextualDrawerMigration,
    currentSessionId,
    cycleSpeed,
    deleteDialogOpen,
    deleteSaveKnowledge,
    deleteSession,
    deleteTargetIds,
    deployPending,
    deployStatus,
    detectedSshUrls,
    devPreviewEnabled,
    workflowVizEnabled,
    doShare,
    doShareExternal,
    doSharePayload,
    drawerCommands,
    drawerOpen,
    drawerPanel,
    drawerTab,
    emitOpusConsult,
    emitOpusDismissed,
    enterShareSelection,
    exitSelectionMode,
    explainPayload,
    fetchSessionHealth,
    fetchSshTargets,
    fileInputProps,
    formatMultiMessageMarkdown,
    geminiEnabled,
    geoNeeded,
    getCheckedKeys,
    goToChat,
    guideEnabled,
    handleActAs,
    handleContinueSession,
    handleExpandedChange,
    handleExplainHere,
    handleExplainToNewSession,
    handleExplainToSession,
    handleLogout,
    handleNewSession,
    handleReplayCoach,
    handleRunPlanHere,
    handleSelectSession,
    handleSendToSession,
    handleShareAsPdf,
    handleShareFileExternal,
    handleShareFilePdf,
    handleShareMedia,
    handleShareSingle,
    handleShareToNewSession,
    handleSpeakText,
    handleThemeSwitch,
    handleWorkOnItem,
    hasAttachments,
    idleSet,
    interAgentSharingEnabled,
    isAdmin,
    isAdminContext,
    isSessionSoundOn,
    kebabOpen,
    kebabRef,
    keyboardStripOpen,
    lastDeploy,
    leftShadowRef,
    leftSliverRef,
    lessonGeneratorEnabled,
    loadOlderMessages,
    loading,
    logbookEnabled,
    logbookOpen,
    logbookScreenshot,
    logbookText,
    memosEnabled,
    messages,
    modelSelectorEnabled,
    modelSheetOpen,
    onboardingGithubConnected,
    onboardingGithubLogin,
    onboardingGithubOauthEnabled,
    onboardingNeeded,
    openBrowser,
    openDeleteDialog,
    openDrawerH,
    openFiles,
    openInTab,
    openListDeleteDialog,
    openLogbook,
    openResetDialog,
    openSettings,
    openTabs,
    opsPanelEnabled,
    opusPrompt,
    opusSuggestionSource,
    pendingHighCostSend,
    pendingSharePayloadRef,
    pendingShareTargetRef,
    pendingShareTextRef,
    pinProject,
    pinSessionToList,
    plansDirName,
    plansTabEnabled,
    preferWhisper,
    previewVoice,
    profileLoaded,
    projectChipsEnabled,
    projectDocsDirName,
    providerErrors,
    providerInfo,
    pullDistance,
    pulling,
    pulseEnabled,
    readerModeEnabled,
    refreshing,
    registryDirName,
    registryModels,
    removeAttachment,
    renameOpen,
    renameSession,
    renameValue,
    requestSshSummary,
    resetDialogOpen,
    resetSaveKnowledge,
    rightShadowRef,
    rightSliverRef,
    roadsTabEnabled,
    sandboxFsEnabled,
    selectedMessageIds,
    selectedVoiceId,
    selectedVoiceName,
    selectionMode,
    selectionPurpose,
    sendAudioChunk,
    sendText,
    sendTextWithOverride,
    sendWithAttachments,
    sessionHealth,
    sessionMeta,
    sessions,
    setActiveRole,
    setBrowserConsentPrompt,
    setBrowserTierGatePrompt,
    setChatAtBottom,
    setChatPanelEl,
    setChatTab,
    setConsentNeeded,
    setDeleteDialogOpen,
    setDeleteSaveKnowledge,
    setDeleteTargetIds,
    setDrawerOpen,
    setDrawerTab,
    setExplainPayload,
    setGeoNeeded,
    setKebabOpen,
    setKeyboardStripOpen,
    setLogbookScreenshot,
    setLogbookText,
    setModelSheetOpen,
    setOnboardingNeeded,
    setOpusPrompt,
    setPendingHighCostSend,
    setRenameOpen,
    setRenameValue,
    setResetDialogOpen,
    setResetSaveKnowledge,
    setShareTargetOpen,
    setSlashPopover,
    setSleepConfirm,
    setSshPickerOpen,
    setSttLang,
    setViewportFocus,
    setVoice,
    setVoicePickerOpen,
    shareTargetOpen,
    slashPopover,
    sleepConfirm,
    socketRef,
    speaking,
    speakingSourceKey,
    sshConnect,
    sshConnected,
    sshDisconnect,
    sshExitTerminal,
    sshOutput,
    sshOutputEpoch,
    sshPickerOpen,
    sshResize,
    sshSendInput,
    sshTarget,
    sshTargets,
    sshTermRef,
    sshTerminalEnabled,
    sshVoiceHistory,
    stop,
    sttLang,
    sttLanguage,
    summarizing,
    switchTabWithHistory,
    tags,
    tasksTabEnabled,
    tempDocsDirName,
    theme,
    toggleAutoDocsPaused,
    toggleMessageSelection,
    toggleProject,
    toggleSessionSound,
    toolActivities,
    inflightToolRowId,
    transcribeAudio,
    triggerFilePicker,
    ttsSpeed,
    unpinProject,
    unpinSessionFromList,
    updateAutoDocsThreshold,
    updateSessionTuning,
    usage,
    usagePanelEnabled,
    userAutoDocsThreshold,
    userEmail,
    userInitial,
    userProvisioningStatus,
    visibleDots,
    voicePickerOpen,
    voices,
    wasmActive
  } = props;


  // Context % for the active session dot — the server-computed occupancy union
  // (context-fullness Phase 0b), NEVER derived from raw tokens/window (SE-09).
  // Projected renders as `~NN%` on the dot (display-only mid-turn estimate).
  const activeFullness = useContextFullnessStore((s) => (currentSessionId ? s.bySession[currentSessionId] : undefined));
  const activeDotContextPct =
    activeFullness && (activeFullness.kind === 'measured' || activeFullness.kind === 'projected')
      ? activeFullness.pct
      : undefined;
  const activeDotProjected = activeFullness?.kind === 'projected';

  // P3: session-dots strip, rendered into ChatArea's composer top-bar LEFT zone via
  // the `sessionDots` render-prop (was the standalone fixed dots strip below the
  // composer).
  // Phase 3 (agents-drawer-carousel-reorg.md): the strip maps `visibleDots` — the SAME
  // memo the swipe carousel + slivers consume (getVisibleDots: openTabs minus slept,
  // slept-but-awaiting RE-included). Slept sessions leave the strip entirely (still
  // reachable via the Agents drawer's recent filter); they re-surface only when the
  // agent needs you, because the selector returns 'awaiting' (which outranks 'idle').
  // Mapping the shared array makes "the dots strip" and "the swipe set" provably equal.
  // #1433 r4: dots-ONLY row (no active-name label — the name lives in the top bar).
  const sessionDotsEl = (
    <div class="session-dots-strip" aria-label="Open sessions">
      {/* Phase 2 (agents-drawer-carousel-reorg.md 2a): the leading 🏠 Home dot is
          REMOVED. The session list is now the Agents bottom-nav drawer (👥), not a
          routable view — there is no "home" destination to navigate to from the dots
          strip. The strip is open-agents-only; chat IS home. */}
      {visibleDots.map(({ tabId, dotState }, idx) => {
        return (
          <SessionDot
            key={tabId}
            tabId={tabId}
            dotState={dotState}
            tabIndex={idx}
            onSwitch={switchTabWithHistory}
            onRequestSleep={(sessionId) => setSleepConfirm({ sessionId })}
            sleepable={openTabs.length >= 2}
            onRename={dotState === 'active' ? () => {
              const cur = sessions.find((s) => s.id === currentSessionId);
              setRenameValue(cur?.name || '');
              setRenameOpen(true);
            } : undefined}
            contextPct={dotState === 'active' ? activeDotContextPct : undefined}
            contextProjected={dotState === 'active' ? activeDotProjected : undefined}
            resetThreshold={dotState === 'active' ? userAutoDocsThreshold : undefined}
            onOpenContextBar={() => window.dispatchEvent(new CustomEvent('va-open-contextbar'))}
          />
        );
      })}
    </div>
  );

  // P6: playback transport — relocated from the top-header action row into the
  // composer top-bar CENTER gap. Passed to ChatArea as a render-prop. P4 (#1652):
  // PlaybackFab is now a STOP-ONLY ⏹ — it self-hides unless the shared useTTS channel
  // is actively speaking (streaming auto-read / SSH summary / Links read), so the
  // center zone is empty otherwise. Manual reads use the per-message 🔊; TTS speed
  // moved to Settings.
  const playbackTransportEl = (!sshConnected && !selectionMode && currentSessionId) ? (
    <PlaybackFab inline speaking={speaking} onStop={stop} />
  ) : null;

  // Phase 5: composer-slot read-aloud bar. The manual per-message read (docTtsStore,
  // channel A) surfaces its DocTtsPlaybackBar in the BottomComposer pill slot for the
  // CURRENT session, unless the user collapsed it (barCollapsed → re-show chip) or the
  // profile uses browser TTS (no server bar there, matching ChatArea's ▶ gate). The
  // streaming auto-read (channel B) keeps its separate ⏹ PlaybackFab above — untouched.
  const slotTtsProvider = useSettingsStore((s) => s.ttsProvider);
  const chatTtsSlot = useChatTtsSlot(currentSessionId);
  const readAloudActive = chatTtsSlot.active && !chatTtsSlot.collapsed && slotTtsProvider !== 'browser';

  // Bottom-nav P1: the session ContextBar moves out of the top bar into the ChatArea
  // control row (between 🔍 find and the turn-nav). Built here as a render-prop element
  // so App keeps ownership of the tuning handlers. See plans/bottom-nav-shell.md §2b.
  const contextBarEl = (() => {
    const sess = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : null;
    return (
      <ContextBar
        variant="navslot"
        checkedKeys={getCheckedKeys()}
        sessionMeta={currentSessionId ? sessionMeta[currentSessionId] : undefined}
        usage={usage}
        health={sessionHealth}
        loading={sessionHealth === null && !!currentSessionId}
        hidePct={activeDotContextPct != null && activeDotContextPct > 0}
        providerInfo={providerInfo}
        currentSessionId={currentSessionId}
        onToggleAutoDocsPaused={toggleAutoDocsPaused}
        onThresholdChange={updateAutoDocsThreshold}
        sessionModel={sess?.model ?? null}
        effort={sess?.effort ?? null}
        models={registryModels}
        enable1mContext={sess?.enable1mContext ?? false}
        onEffortChange={currentSessionId ? (effort) => {
          updateSessionTuning(currentSessionId, { effort });
          const hint =
            effort === 'low' ? 'fastest, lightest reasoning' :
            effort === 'medium' ? 'balanced' :
            effort === 'high' ? 'deeper reasoning' :
            effort === 'xhigh' ? 'slower, deeper reasoning' :
            'slowest, deepest reasoning';
          const label = effort === 'xhigh' ? 'X-High' : effort === 'max' ? 'Max' : effort.charAt(0).toUpperCase() + effort.slice(1);
          coachShow({ id: 'effort', kind: 'toast', body: `Effort: ${label} — ${hint}`, ttlMs: 2500 });
        } : undefined}
        on1mContextChange={currentSessionId ? (enable) => {
          updateSessionTuning(currentSessionId, { enable1mContext: enable });
        } : undefined}
      />
    );
  })();

  // r11: browser + dev-preview — chat-scoped icon launchers added to the cluster row
  // (after find). Icon-only + borderless (.ccl-icon-btn, styled like the context pill).
  // App owns the handlers; DevPreviewButton compact mode reuses its FSM store wiring.
  const clusterBrowserEl = (browserPanelEnabled && !selectionMode && currentSessionId) ? (
    <button
      type="button"
      class="ccl-icon-btn"
      onClick={openBrowser}
      title="Open browser"
      aria-label="Open browser"
    >{'\u{1F310}'}</button>
  ) : null;
  const clusterDevPreviewEl = (isAdmin && devPreviewEnabled && !selectionMode && currentSessionId) ? (
    <DevPreviewButton sessionId={currentSessionId} compact />
  ) : null;
  // agent-runs §4: Agent Runs — cluster icon right of dev-preview. The icon RENDERS ITSELF
  // AWAY when the session has no runs (badge==='idle'), so the cluster stays uncluttered for
  // the sessions that never launch a workflow — that's why there is no `hasRuns` check here.
  const clusterWorkflowEl = (isAdmin && workflowVizEnabled && !selectionMode && currentSessionId) ? (
    <WorkflowStatusIcon sessionId={currentSessionId} onOpen={() => openDrawerH('runs')} />
  ) : null;
  // r17: SSH connection — cluster icon right of dev-preview. Tap → SSH target picker;
  // tinted when connected. Gated on sshTerminalEnabled.
  const clusterSshEl = (sshTerminalEnabled && !selectionMode && currentSessionId) ? (
    <button
      type="button"
      class={`ccl-icon-btn${sshConnected ? ' ccl-ssh--connected' : ''}`}
      onClick={() => { fetchSshTargets(); setSshPickerOpen(true); }}
      title={sshConnected ? 'SSH connected — manage connection' : 'Connect via SSH'}
      aria-label="SSH connection"
    >{'\u{1F5A5}'}</button>
  ) : null;
  // r15/r17: settings gear — relocated from the old top-right chat header into the cluster,
  // between SSH and find. r17: opens the Settings screen DIRECTLY (was the GearMenu kebab).
  // Settings is moving into the unified drawer (unified-drawer.md §8).
  // Settings gear moved to the bottom nav Settings slot — no longer in the composer cluster.
  const clusterGearEl = null;

  // ── Agents-drawer (agents-drawer-carousel-reorg.md) ──────────────────────────
  // Phase 2 realized D1 = REPLACE: SessionListView now mounts EXACTLY ONCE, inside the
  // unified DrawerHost as the 'sessions' panel (the leftmost Agents bottom-nav button →
  // openDrawerH('sessions')). The legacy slide-back-to-list instance is removed (chat is
  // home). This shared prop block (extracted in Phase 1) feeds the single drawer
  // instance via drawerSessionListProps below.
  const sessionListProps = {
    currentSessionId,
    onSelectSession: handleSelectSession,
    onFiles: openFiles,
    onRename: renameSession,
    onDeleteRequest: openListDeleteDialog,
    onSwitchToMessage: (sessionId: string, messageId: string) => {
      openInTab(sessionId); goToChat();
      if (messageId === 'last-prompt') window.dispatchEvent(new CustomEvent('va:scroll-to-last-prompt'));
    },
    onSettings: openSettings,
    onContinue: handleContinueSession,
    isSessionSoundOn,
    onToggleSound: toggleSessionSound,
    connected,
    speaking,
    toolActivities,
    deployStatus,
    deployPending,
    theme,
    onThemeSwitch: handleThemeSwitch,
    userEmail,
    onLogout: handleLogout,
    onBrowser: () => { openBrowser(); },
    sshConnected,
    onSshConnect: sshTerminalEnabled ? () => { fetchSshTargets(); setSshPickerOpen(true); } : undefined,
    onSshDisconnect: sshDisconnect,
    memosEnabled,
    lessonGeneratorEnabled,
    billingEnabled,
    isAdmin: contextualDrawerMigration && isAdmin ? isAdmin : false,
    onDoctor: contextualDrawerMigration && isAdmin ? () => sendText('/doctor-voice') : undefined,
    onRebuild: contextualDrawerMigration && isAdmin ? () => sendText('/doctor-voice rebuild') : undefined,
    rebuildEnabled: contextualDrawerMigration && isAdmin,
    onNewSession: handleNewSession,
    plansTabEnabled,
    roadsTabEnabled,
    onPinSession: pinSessionToList,
    onUnpinSession: unpinSessionFromList,
    onLogbook: logbookEnabled ? openLogbook : undefined,
    browserPanelEnabled,
    pulseEnabled,
    guideEnabled,
    sandboxFsEnabled,
  };

  // Drawer-instance overrides: any callback that ROUTES TO CHAT (goToChat) must close
  // the Agents drawer first, else the chat view navigates UNDERNEATH the still-open
  // sheet (DrawerHost keeps rendering while phase==='open'). Mirrors the Tasks panel's
  // `(wi) => { closeDrawer(); handleWorkOnItem(wi); }` pattern (D2-a). onSettings/onFiles
  // are NOT wrapped — those are open→open panel swaps (an acceptance criterion).
  const closeThen = <A extends unknown[]>(fn?: (...a: A) => void) =>
    fn ? (...a: A) => { closeDrawer(); fn(...a); } : undefined;
  const drawerSessionListProps = {
    ...sessionListProps,
    onSelectSession: (sid: string) => { closeDrawer(); handleSelectSession(sid); },
    onContinue: closeThen(handleContinueSession),
    onNewSession: closeThen(handleNewSession),
    onSwitchToMessage: (sessionId: string, messageId: string) => {
      closeDrawer();
      openInTab(sessionId); goToChat(sessionId);
      if (messageId === 'last-prompt') window.dispatchEvent(new CustomEvent('va:scroll-to-last-prompt'));
    },
  };

  return (
    <div
      class={`app-container${sshConnected ? ' ssh-mode' : ''}`}
      data-va-variant="dark"
      ref={stampDesignTokens}
    >
      {/* Phase 0 of settings-zustand-migration: dev-only consumer of
          useSettingsStore via the hook path — verifies preact/compat's
          useSyncExternalStore shim doesn't infinite-rerender (plan risk R1).
          Removed when Phase 1 lands a real settings consumer. */}
      {import.meta.env.DEV && <SettingsStoreDevProbe />}

      {/* Hidden file input for image picker */}
      <input {...fileInputProps} />

      {/* Pull-to-refresh indicator */}
      {(pulling || refreshing) && (
        <div
          class={`ptr-indicator${refreshing ? ' refreshing' : ''}${pullDistance >= 80 ? ' ready' : ''}`}
          style={{ transform: `translateY(${pullDistance - 40}px)` }}
        >
          <div class="ptr-spinner" />
        </div>
      )}

      {/* Chat-as-home (Phase 2, agents-drawer-carousel-reorg.md 2c): the list is no
          longer a routable VIEW — it lives ONLY in the Agents drawer (below). The legacy
          left slide-back panel that mounted a second SessionListView is REMOVED so
          SessionListView mounts exactly ONCE (in the drawer), resolving the Phase-1
          double-listener. The view-slider wrapper is kept for the chat panel's stacking
          context (.view-panel will-change:transform anchors the bottom-composer +
          bottom-nav); it is permanently pinned to chat. */}
      <div class="view-slider show-chat">

        {/* ── Panel 2: Chat ── */}
        <div class="view-panel chat-panel" ref={setChatPanelEl}>
      <div class={`chat-panel-inner${sshConnected ? ' ssh-mode' : ''}`}>
      <div class={`top-bar chat-top-bar chat-top-bar--unified${sshConnected ? ' ssh-top-bar' : ''}`}>
        {sshConnected ? (
          <>
            <div class="top-bar-left">
              {/* In SSH mode the back arrow EXITS the terminal via the single atomic
                  store action (Phase 2): sshExitTerminal() transitions the FSM
                  connected → disconnecting, which immediately clears the sshConnected
                  surface gate (no stranding / cross-session contamination), then emits
                  SSH_DISCONNECT. goToChat() navigates now rather than waiting for the
                  server round-trip. See plans/ssh-terminal-fsm-migration.md. */}
              <button class="hamburger-btn" onClick={() => { setKebabOpen(false); sshExitTerminal(); goToChat(); }} title="Exit terminal" aria-label="Exit terminal">&#8592;</button>
            </div>
            <h1>{`${sshTarget?.user}@${sshTarget?.host}`}</h1>
          </>
        ) : (
          <>
            {/* === ROW 1: identity + model + token ===
                Bottom-nav P1: the back arrow is removed — 🏠 Home in the BottomNav now
                navigates to the session list. The top bar keeps the agent name (+ admin
                pill + ReaderToggle); the rest is reserved empty space for future
                intent-capture (plans/bottom-nav-shell.md §2b). */}
            <div class="utb-row-1">
              <div class="utb-identity">
                {(() => {
                  const s = sessions.find((s) => s.id === currentSessionId);
                  const model = s?.model ?? null;
                  const isRelay = s?.isRelay ?? false;
                  const badge = isRelay ? { l: 'SR', c: '#4ecca3' }
                    : !model ? { l: 'S', c: '#4ec9b0' }  // cascade default = Sonnet
                    : model.includes('opus') ? { l: 'O', c: '#bc8cff' }
                    : model.includes('sonnet') ? { l: 'S', c: '#56a0d4' }
                    : model.includes('haiku') ? { l: 'H', c: '#8888aa' }
                    : model.includes('gemini') ? { l: 'G', c: '#34a853' }
                    : { l: '?', c: '#666' };
                  return (
                    <>
                      <span class="chat-header-badge" style={{ background: badge.c }}>{badge.l}</span>
                      <div class="utb-name-stack">
                        {/* P4b — identity migration: the editable session name moved to the
                            bottom-bar identity chip (active dot + tap-to-rename label). The top
                            header now carries read/status only — model badge, connection signal,
                            admin pill, and the rolling summary (.utb-context-tag) below. */}
                        <div class="utb-name-row">
                          <span
                            class={`brand-signal brand-signal--inline ${!connected ? 'disconnected' : deployStatus === 'rebuildScheduled' ? 'rebuild-scheduled' : (speaking || busy || toolActivities.length > 0) ? 'operating' : 'connected'}`}
                            title={!connected ? 'Disconnected' : deployStatus === 'rebuildScheduled' ? formatDeployPending(deployPending) : (speaking || busy || toolActivities.length > 0) ? 'Agents active' : 'Connected'}
                          />
                          {/* r11: editable agent name restored to the top bar (was removed in the
                              P4b identity migration). Tap → the existing inline rename bar. */}
                          {currentSessionId && (
                            <button
                              type="button"
                              class="utb-name-label"
                              onClick={() => { setRenameValue(s?.name || ''); setRenameOpen(true); }}
                              title="Tap to rename this agent"
                            >
                              <span class="utb-name-text">{s?.name || s?.previewText || 'Untitled'}</span>
                              <span class="utb-name-edit" aria-hidden="true">{'✎'}</span>
                            </button>
                          )}
                          {isAdminContext && (
                            <span class="admin-context-pill" title={s?.actAsPersona ? `Acting as ${s.actAsPersona}` : 'Admin context active'}>
                              {s?.actAsPersona ? `as: ${s.actAsPersona.split('@')[0]}` : 'Admin'}
                            </span>
                          )}
                        </div>
                        {s?.contextTag && (
                          <div class="utb-context-tag">{s.contextTag}</div>
                        )}
                        {/* Latest captured intent (model-meta-hardening Phase 5):
                            the most recent classified turn's work category +
                            the model that ran. Live via LATEST_INTENT_UPDATED;
                            restored server-authoritatively on reload from the
                            SESSIONS_LIST snapshot. Distinct from contextTag
                            (topic) — this is the work CATEGORY. */}
                        {(() => {
                          const intentLabel = intentFriendlyLabel(s?.latestIntentType);
                          if (!intentLabel) return null;
                          const ran = modelShortName(s?.latestIntentModel ?? null, false);
                          return (
                            <div class="utb-intent-tag" title={`Latest intent: ${intentLabel}${ran ? ` · ${ran}` : ''}`}>
                              <span class="utb-intent-label">{intentLabel}</span>
                              {ran && <span class="utb-intent-model">{ran}</span>}
                            </div>
                          );
                        })()}
                        {projectChipsEnabled && s?.activeProjects && s.activeProjects.length > 0 && currentSessionId && (
                          <div class="utb-chips-dots-row">
                            <ProjectChips
                              sessionId={currentSessionId}
                              projects={s.activeProjects}
                              pinnedProject={s.pinnedProject ?? null}
                              allProjectTags={tags}
                              onPin={pinProject}
                              onUnpin={unpinProject}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
              {/* In-session model picker — gated OFF on every profile (#1071). New-session
                  model choice still lives in the FAB long-press. Code kept for re-enable. */}
              {modelSelectorEnabled && (
              <button
                type="button"
                class="model-pill-btn"
                onClick={() => setModelSheetOpen(true)}
                title="Switch model or get a second opinion"
              >
                {(() => {
                  const sess = sessions.find((s) => s.id === currentSessionId);
                  const m = sess?.model ?? '';
                  let name = 'Sonnet';
                  if (m.includes('opus')) name = 'Opus';
                  else if (m.includes('haiku')) name = 'Haiku';
                  else if (m.includes('gemini')) name = 'Gemini';
                  const eff = sess?.effort;
                  const effLabel = eff === 'xhigh' ? 'X-High' : eff === 'max' ? 'Max' : eff === 'high' ? 'High' : eff === 'medium' ? 'Med' : eff === 'low' ? 'Low' : null;
                  return (
                    <>
                      {name}
                      {effLabel && <span class={`model-pill-effort${eff === 'xhigh' || eff === 'max' ? ' model-pill-effort--high' : ''}`}>· {effLabel}</span>}
                      {' ▾'}
                    </>
                  );
                })()}
              </button>
              )}
              {/* Trailing action cluster — moved here from a separate second
                  toolbar row to keep the chat header as a single row on mobile.
                  Right-edge order: 🔍 find · ▶ playback (inline) · context pill · ⚙ menu (far right). */}
              <div class="utb-row-1-actions">
                {readerModeEnabled && activeView === 'chat' && !!currentSessionId && (
                  <ReaderToggle surfaceId={`chat-${currentSessionId}`} coachAnchor />
                )}
                {/* P5: find-in-conversation trigger relocated to the composer nav cluster
                    (ChatArea .ctb-nav-zone), co-located with the turn-nav. Removed from this
                    top-header action row per §4B.1. The find FIELD now reuses the bottom
                    composer pill (BottomFindBar, #1433); Ctrl+F still opens it. */}
                {/* P6: the playback transport moved OUT of this top-header action row into
                    the composer top-bar center gap (ChatArea .ctb-transport-zone) — persistent,
                    thumb-zone, off the hardest-to-reach corner. Built as `playbackTransportEl`. */}
                {/* Bottom-nav P1: the ContextBar relocated to the ChatArea control row
                    (built as `contextBarEl`, passed via the `contextBar` prop), and the
                    ⚙ gear TRIGGER button was removed — the GearMenu now opens via the
                    BottomNav's ⋯ More slot (onMore → setKebabOpen(true)). The GearMenu
                    component + .kebab-wrapper (for click-outside) stay mounted here.
                    See plans/bottom-nav-shell.md §2b/§4. */}
                <div class="kebab-wrapper" ref={kebabRef}>
                  {kebabOpen && (
                    <GearMenu
                      placement="sheet"
                      userEmail={userEmail}
                      onClose={() => setKebabOpen(false)}
                      inSession={!!currentSessionId}
                      isAdmin={isAdmin}
                      memosEnabled={memosEnabled}
                      lessonGeneratorEnabled={lessonGeneratorEnabled}
                      billingEnabled={billingEnabled}
                      keyboardStripOpen={keyboardStripOpen}
                      onToggleKeyboard={() => setKeyboardStripOpen(!keyboardStripOpen)}
                      sshConnected={sshConnected}
                      onSshConnect={sshTerminalEnabled ? () => { fetchSshTargets(); setSshPickerOpen(true); } : undefined}
                      onSshDisconnect={sshDisconnect}
                      onResetSession={openResetDialog}
                      onDeleteSession={currentSessionId ? openDeleteDialog : undefined}
                      onSettings={openSettings}
                      onBrowser={browserPanelEnabled ? openBrowser : undefined}
                      onFiles={openFiles}
                      onUsage={() => openDrawerH('usage')}
                      onMemos={memosEnabled ? () => { window.location.href = '/memos'; } : undefined}
                      onLessons={lessonGeneratorEnabled ? () => { window.location.href = '/lessons'; } : undefined}
                      onLogbook={logbookEnabled ? () => { setKebabOpen(false); openLogbook(); } : undefined}
                      onSessionsDrawer={() => { setDrawerTab('sessions'); setDrawerOpen(true); }}
                      guideEnabled={guideEnabled}
                      pulseEnabled={pulseEnabled}
                      onReplayCoach={handleReplayCoach}
                      onDoctor={isAdmin ? () => sendText('/doctor-voice') : undefined}
                      onRebuild={isAdmin ? () => sendText('/doctor-voice rebuild') : undefined}
                      rebuildEnabled={isAdmin}
                      onActAs={isAdmin && currentSessionId ? handleActAs : undefined}
                      actAsPersona={sessions.find((s) => s.id === currentSessionId)?.actAsPersona}
                      devPreviewEnabled={isAdmin && devPreviewEnabled}
                      devPreviewSessionId={currentSessionId}
                      onSignOut={handleLogout}
                      wasmActive={wasmActive}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* === ROW 2: active plan chip only ===
                Beetle (logbook) accessible via ⚙ GearMenu; reader toggle moved to
                Row-1 actions; session dots moved into utb-name-row. CSS :empty
                collapses this row when no plan is active (~0px for most sessions). */}
            <div class="utb-row-2">
              {(() => {
                const s = sessions.find((x) => x.id === currentSessionId);
                return s?.activePlan ? (
                  <PlanChip
                    title={s.activePlan.title}
                    project={s.activePlan.project}
                    command={s.activePlan.command}
                    onRunHere={handleRunPlanHere}
                    onShare={doShare}
                  />
                ) : null;
              })()}
            </div>
          </>
        )}
      </div>

      {/* Chat find-in-page bar relocated (#1433): find now reuses the bottom
          composer pill (BottomFindBar) instead of a separate top-sliding bar,
          so the search field sits in the thumb zone. See the BottomComposer
          `findBar` / `findActive` props above. The old `.chat-find-bar-wrap`
          top bar and the ChatFindBar component are no longer rendered here. */}

      {/* Inline rename dialog */}
      {renameOpen && currentSessionId && (
        <div class="chat-rename-bar">
          <input
            type="text"
            class="chat-rename-input"
            value={renameValue}
            onInput={(e) => setRenameValue((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                renameSession(currentSessionId, renameValue.trim());
                setRenameOpen(false);
              } else if (e.key === 'Escape') setRenameOpen(false);
            }}
            autoFocus
            placeholder="Session name..."
          />
          <button class="chat-rename-save" onClick={() => {
            renameSession(currentSessionId, renameValue.trim());
            setRenameOpen(false);
          }}>Save</button>
          <button class="chat-rename-cancel" onClick={() => setRenameOpen(false)}>Cancel</button>
        </div>
      )}

      {/* ── Selection mode top bar (Opus or Share) ── */}
      {selectionMode && (
        <div class="opus-selection-topbar">
          <span class="opus-selection-label">
            {selectionPurpose === 'share'
              ? `Share${selectedMessageIds && selectedMessageIds.size > 0 ? ` • ${selectedMessageIds.size} selected` : ''}`
              : `Select context for ${CONSULT_PROVIDERS.find(p => p.id === consultTarget)?.label ?? 'Opus'}${selectedMessageIds && selectedMessageIds.size > 0 ? ` • ${selectedMessageIds.size} selected` : ''}`}
          </span>
          <button class="opus-selection-cancel" onClick={() => {
            if (selectionPurpose === 'opus' && opusSuggestionSource === 'classifier' && currentSessionId && opusPrompt) {
              emitOpusDismissed(currentSessionId, opusPrompt);
            }
            exitSelectionMode();
          }} title="Cancel">✕</button>
        </div>
      )}

      <UnifiedDrawer
        open={drawerOpen}
        tab={drawerTab}
        onTabChange={setDrawerTab}
        onClose={() => setDrawerOpen(false)}
        projectTags={tags}
        currentSessionId={currentSessionId}
        lastDeploy={lastDeploy}
        onSwitch={openInTab}
        onRename={renameSession}
        onDelete={deleteSession}
        onNewSession={handleNewSession}
        onSwitchToMessage={(sessionId, _messageId) => { openInTab(sessionId); }}
        commands={drawerCommands}
        onRunCommand={(cmd) => sendText(cmd)}
        onOpenSettings={openSettings}
        onLogout={handleLogout}
        onDoctor={() => sendText('/doctor-voice')}
        onRebuild={() => sendText('/doctor-voice rebuild')}
        sshConnected={sshConnected}
        onSshConnect={sshTerminalEnabled ? () => { fetchSshTargets(); setSshPickerOpen(true); } : undefined}
        onSshDisconnect={sshDisconnect}
        onToggleLanguage={() => {
          const newLang = sttLanguage === 'en' ? 'bg' : 'en';
          useSettingsStore.getState().setSttLanguage(newLang);
          setSttLang(newLang === 'bg' ? 'bg-BG' : 'en-US');
        }}
        isAdmin={isAdmin}
        memosEnabled={memosEnabled}
      />

      {/* Swipe edge shadows + adjacent-session slivers — positioned absolutely, outside the slide wrapper */}
      <div ref={leftShadowRef}  class="swipe-edge-shadow swipe-edge-shadow--left" />
      <div ref={rightShadowRef} class="swipe-edge-shadow swipe-edge-shadow--right" />
      <div ref={leftSliverRef}  class="swipe-sliver swipe-sliver--left">
        <div class="swipe-sliver-badge" />
      </div>
      <div ref={rightSliverRef} class="swipe-sliver swipe-sliver--right">
        <div class="swipe-sliver-badge" />
      </div>

      {sshTerminalEnabled && sshConnected && (
        <SshTerminal output={sshOutput} outputEpoch={sshOutputEpoch} onInput={sshSendInput} onResize={sshResize} handleRef={sshTermRef}
          hidden={sshTarget?.isSelf && consoleView} />
      )}
      {(!sshConnected || (sshTarget?.isSelf && consoleView)) && (
        /* Slide wrapper — only the chat content translates during swipe; FABs stay static */
        <div ref={chatSlideRef} class="chat-slide-wrapper">
        {!currentSessionId && openTabs.length === 0 ? (
          <div class="chat-area chat-empty-state">
            <div class="chat-empty-icon">&#128172;</div>
            <div class="chat-empty-title">No active session</div>
            <div class="chat-empty-hint">Open <strong>👥 Agents</strong> below to pick a session, or tap <strong>+</strong> to start a new one.</div>
          </div>
        ) : consoleView ? (
          <ConsoleView messages={messages} toolActivities={toolActivities} sessionMeta={currentSessionId ? sessionMeta[currentSessionId] : undefined} fullness={currentSessionId ? activeFullness : undefined} />
        ) : (
          <ChatArea
            speaking={speaking}
            speakingSourceKey={speakingSourceKey}
            sessionId={currentSessionId ?? undefined}
            isAdmin={isAdmin}
            onLoadOlder={loadOlderMessages}
            onSpeakText={handleSpeakText}
            onStopSpeaking={stop}
            canPlayTts={chatTtsAllowed}
            onViewportFocusChange={setViewportFocus}
            activeTab={chatTab}
            onTabChange={setChatTab}
            selectionMode={selectionMode}
            selectedMessageIds={selectedMessageIds}
            onToggleMessageSelection={toggleMessageSelection}
            userInitial={userInitial}
            onFollowLatestChange={setChatAtBottom}
            onShareSingle={handleShareSingle}
            onEnterShareSelection={enterShareSelection}
            onShareMedia={handleShareMedia}
            toolActivities={toolActivities}
            inflightToolRowId={inflightToolRowId}
            onBack={!sshConnected ? () => { setKeyboardStripOpen(false); openDrawerH('sessions'); } : undefined}
            isAutonomous={isAgentSource((sessions.find((s) => s.id === currentSessionId) as any)?.source)}
            autonomousLabel={(sessions.find((s) => s.id === currentSessionId) as any)?.chunkTitle || (sessions.find((s) => s.id === currentSessionId) as any)?.planName || undefined}
            providerError={currentSessionId ? providerErrors.get(currentSessionId) ?? null : null}
            onRetryWithProvider={(text, provider) => { sendTextWithOverride(text, provider); }}
            onDismissProviderError={currentSessionId ? () => clearProviderError(currentSessionId) : undefined}
            onOpenSshTerminal={sshTerminalEnabled ? () => { fetchSshTargets(); setSshPickerOpen(true); } : undefined}
            onOpenSettings={openSettings}
            /* P4b: render the identity chip whenever there's a session (was >= 2).
               Single-session shows one active dot + its name (the identity + rename
               affordance that migrated down from the removed top-header name). The
               free-tier exclusion is dropped for the same reason — identity must never
               be lost. The lone dot is non-sleepable via the sleepable prop above. */
            sessionDots={(!sshConnected && openTabs.length >= 1) ? sessionDotsEl : undefined}
            /* P5: find-in-conversation lives in the nav cluster now (it IS in-chat navigation). */
            onToggleFind={currentSessionId ? () => (chatFind.isOpen ? chatFind.closeFind() : chatFind.openFind()) : undefined}
            findActive={chatFind.isOpen}
            /* Phase 5: the chat read-aloud bar now lives in the composer pill slot
               (below), so suppress ChatArea's floating mount on mobile. Desktop
               (DesktopShell) omits this → keeps its floating bar until its fast-follow. */
            suppressFloatingReadAloudBar
            readAloudCollapsed={chatTtsSlot.active && chatTtsSlot.collapsed}
            onShowReadAloudBar={() => useDocTtsStore.getState().setBarCollapsed(false)}
            /* r7: persistent playback transport — now the center member of the
               .composer-cluster-row (context + playback + find) that floats above the dots. */
            playbackTransport={playbackTransportEl}
            /* Bottom-nav r7: ContextBar moves back into ChatArea as the leading member of
               the new floating cluster row (was the BottomNav leading slot in r6). The
               bottom-left nav slot is now Usage. */
            clusterContext={contextBarEl}
            /* r11: browser + dev-preview icon launchers — icon-only, borderless, styled
               like the context pill. Rendered in the cluster after find. */
            clusterBrowser={clusterBrowserEl}
            clusterDevPreview={clusterDevPreviewEl}
            clusterWorkflow={clusterWorkflowEl}
            clusterSsh={clusterSshEl}
            clusterGear={clusterGearEl}
          />
        )}
        </div> /* end chat-slide-wrapper */
      )}
      {sshTerminalEnabled && sshConnected && !(sshTarget?.isSelf && consoleView) ? (
        <>
          <SshToolbar onSend={sshSendInput} onScrollPage={(n) => sshTermRef.current?.scrollPages(n)} isProdLocal={!!sshTarget?.isSelf} />
          <SshLinkBar urls={detectedSshUrls} />
          <SshVoiceHistory history={sshVoiceHistory} onResend={sshSendInput} />
          <SshPttButton onTranscribed={sshSendInput} onSummary={requestSshSummary} summarizing={summarizing} speechLang={sttLang || undefined} />
        </>
      ) : (
        <>
        {/* ── Selection bar: Opus or Share ── */}
        {selectionMode && currentSessionId && selectionPurpose === 'share' && (
          <div class="share-selection-bar">
            <span class="share-selection-count">{selectedMessageIds?.size ?? 0} messages</span>
            <button
              class="share-send-btn"
              disabled={(selectedMessageIds?.size ?? 0) === 0}
              onClick={() => {
                const session = sessions.find(s => s.id === currentSessionId);
                const title = session?.name || session?.previewText || undefined;
                const ids = selectedMessageIds ?? new Set<string>();
                doSharePayload({
                  type: 'messages',
                  text: formatMultiMessageMarkdown(ids, title),
                  title,
                  sourceSessionId: currentSessionId ?? undefined,
                  sourceMessageIds: [...ids],
                });
                // selection mode exits after user picks a target in ShareTargetModal
              }}
            >Share ↗</button>
          </div>
        )}
        {selectionMode && currentSessionId && selectionPurpose === 'opus' && (() => {
          const tm = CONSULT_PROVIDERS.find(p => p.id === consultTarget) ?? CONSULT_PROVIDERS[0];
          return (
            <div class="opus-selection-bar">
              <textarea
                class="opus-selection-textarea"
                value={opusPrompt}
                onInput={(e) => setOpusPrompt((e.target as HTMLTextAreaElement).value)}
                placeholder={`What should ${tm.label} reason about?`}
                rows={2}
              />
              <div class="opus-selection-actions">
                <span class="opus-selection-count">{selectedMessageIds?.size ?? 0} ctx</span>
                <button
                  class="opus-ask-btn"
                  style={{ background: tm.color }}
                  disabled={busy || !opusPrompt.trim()}
                  onClick={() => {
                    if (!currentSessionId || !opusPrompt.trim()) return;
                    emitOpusConsult(currentSessionId, opusPrompt.trim(), Array.from(selectedMessageIds ?? []), consultTarget);
                    exitSelectionMode();
                  }}
                  title={`Send to ${tm.label}`}
                >Ask {tm.label} {tm.icon}</button>
              </div>
            </div>
          );
        })()}
        {/* ── Bottom composer — always-visible edge-pinned bar (Phase 2.5-F.4).
             Hidden during Opus selection and autonomous sessions (read-only). ── */}
        {!selectionMode && !isAgentSource((sessions.find((s) => s.id === currentSessionId) as any)?.source) && (() => {
          const inputBarElement = (
            <InputBar
              currentSessionId={currentSessionId}
              onSendText={(text, originSessionId) => {
                // Inject active role as user-turn framing (recency bias > system prompt)
                let prompt = text;
                const currentRole = activeRoleRef.current;
                if (currentRole) {
                  const roleDef = PREDEFINED_ROLES.find(r => r.key === currentRole);
                  if (roleDef) {
                    prompt = `<role name="${roleDef.name}">\n${roleDef.content}\n</role>\n\n${text}`;
                  }
                  setActiveRole(null);
                  activeRoleRef.current = null;
                }
                const doSend = () => {
                  if (hasAttachments) {
                    const finalPrompt = prompt || (attachments.length === 1
                      ? 'What do you see in this image? Describe it and suggest what I might want done with it.'
                      : `I'm sending you ${attachments.length} images. Describe what you see and suggest what I might want done.`);
                    // PR #1217 — same origin-pin route as the text-only branch
                    // below. Without this, an image-only send started on A but
                    // committed after a swipe to B would land on B.
                    sendWithAttachments(finalPrompt, attachments, originSessionId);
                    clearAttachments();
                  } else {
                    // EVENT-DRIVEN: originSessionId is pinned by InputBar at mic-START.
                    // Falls back to live activeTabIdRef inside sendText when absent.
                    sendText(prompt, originSessionId);
                  }
                };
                // High-cost effort gate: every xhigh/max turn requires an explicit confirm.
                // Skip when autoSubmitVoice is active — user opted in to hands-free sending.
                const sess = sessions.find((s) => s.id === currentSessionId);
                const eff = sess?.effort;
                if (!autoSubmitVoice && (eff === 'xhigh' || eff === 'max')) {
                  setPendingHighCostSend({ effort: eff, send: doSend });
                  return;
                }
                doSend();
              }}
              onAttachTap={triggerFilePicker}
              hideAttach
              geminiEnabled={geminiEnabled}
              hasAttachments={hasAttachments}
              onCancel={cancelPrompt}
              speechLang={sttLang || undefined}
              transcribeAudio={transcribeAudio}
              sendAudioChunk={sendAudioChunk}
              preferWhisper={preferWhisper}
              onToggleLang={() => {
                const newLang = sttLanguage === 'en' ? 'bg' : 'en';
                useSettingsStore.getState().setSttLanguage(newLang);
                setSttLang(newLang === 'bg' ? 'bg-BG' : 'en-US');
              }}
            />
          );
          return (
            <BottomComposer
              key={currentSessionId ?? 'no-session'}
              currentSessionId={currentSessionId}
              activeRole={activeRole}
              onRoleChange={setActiveRole}
              onAttachTap={triggerFilePicker}
              inputBar={inputBarElement}
              findActive={chatFind.isOpen}
              findBar={
                <BottomFindBar
                  query={chatFind.query}
                  matchIndex={chatFind.matchIndex}
                  matchCount={chatFind.matchCount}
                  onQueryChange={chatFind.setQuery}
                  onNext={chatFind.goNext}
                  onPrev={chatFind.goPrev}
                  onClose={chatFind.closeFind}
                />
              }
              readAloudActive={readAloudActive}
              readAloudBar={
                <DocTtsPlaybackBar
                  composer
                  onHide={() => useDocTtsStore.getState().setBarCollapsed(true)}
                />
              }
              attachments={attachments}
              attachProcessing={attachProcessing}
              attachError={attachError}
              onRemoveAttachment={removeAttachment}
              initialExpanded={activeComposeExpanded}
              onExpandedChange={handleExpandedChange}
            />
          );
        })()}
        {sleepConfirm && (
          <div class="sleep-confirm-backdrop" onClick={() => setSleepConfirm(null)}>
            <div class="sleep-confirm-sheet" onClick={(e) => e.stopPropagation()}>
              <div class="sleep-confirm-title">Sleep this session?</div>
              <div class="sleep-confirm-sub">
                It leaves the dot strip and stays in your recent list. It pops back if its
                agent needs you.
              </div>
              <div class="sleep-confirm-actions">
                <button
                  class="sleep-confirm-btn sleep-confirm-cancel"
                  onClick={() => setSleepConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  class="sleep-confirm-btn sleep-confirm-sleep"
                  onClick={() => {
                    const sid = sleepConfirm.sessionId;
                    // Sleeping the CURRENT chat must move the operator elsewhere
                    // first (it's about to leave the strip): nearest non-slept
                    // neighbour, else open the Agents drawer so they can pick where
                    // to go (Phase 2: no list view). Navigate BEFORE sleeping so the
                    // target (non-slept) doesn't trip auto-wake and the old session
                    // is already non-current when it enters idleSet.
                    if (sid === currentSessionId) {
                      const target = pickSleepAdvanceTarget(openTabs, sid, idleSet);
                      if (target) switchTabWithHistory(target);
                      else openDrawerH('sessions');
                    }
                    useAgentListStore.getState().sleep(sid);
                    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(50);
                    setSleepConfirm(null);
                  }}
                >
                  Sleep
                </button>
              </div>
            </div>
          </div>
        )}
        </>
      )}
      {/* Playback pill moved into the top-bar right action cluster (inline variant). */}
      {/* Back button is now part of the unified fab-nav-cluster inside ChatArea */}
      {/* ── Floating action cluster (context + keyboard + sound) — hidden during selection mode ── */}
      {!sshConnected && !selectionMode && (
        <FloatingActionCluster
          keyboardStripOpen={keyboardStripOpen}
          onToggleKeyboard={() => setKeyboardStripOpen(!keyboardStripOpen)}
          soundOn={!!(currentSessionId && isSessionSoundOn(currentSessionId))}
          onToggleSound={() => currentSessionId && toggleSessionSound(currentSessionId)}
          showSound={!!currentSessionId}
          sshConnected={sshConnected}
          onSshConnect={sshTerminalEnabled ? () => { fetchSshTargets(); setSshPickerOpen(true); } : undefined}
          onSshDisconnect={sshDisconnect}
          keyboardContent={
            <>
              <CommandPalette
                onSendCommand={(text) => { sendText(text); setKeyboardStripOpen(false); }}
                checkedKeys={getCheckedKeys()}
                totalProjectCount={tags.length}
                projectTags={tags}
                checkedProjects={checkedProjects}
                onToggleProject={toggleProject}
                commands={drawerCommands}
              />
              {voices.length > 0 && currentSessionId && (
                <button
                  type="button"
                  class="utb-icon-btn"
                  onClick={() => setVoicePickerOpen(true)}
                  title={`Voice: ${selectedVoiceName || 'default'}`}
                >{'\u{1F399}'}</button>
              )}
            </>
          }
        />
      )}
      {/* Bottom-nav P1 (plans/bottom-nav-shell.md §2b) — persistent fixed destination bar.
          Mounted inside .chat-panel-inner alongside the composer (same .view-panel
          stacking context, so position:fixed lands at the viewport bottom for the
          active chat panel, like .bottom-composer). Hidden in SSH mode. Slots:
          Agents (👥) → openDrawerH('sessions') — the session list, leftmost;
          Tasks, Files, Ops, Settings → their drawers; More → GearMenu (which now
          also hosts Usage — issue #4 declutter, coach-tooltip-system.md Phase 5). */}
      {!sshConnected && (
        <BottomNav
          onAgents={() => openDrawerH('sessions')}
          onTasks={tasksTabEnabled ? () => openDrawerH('tasks') : undefined}
          onFiles={openFiles}
          onOps={opsPanelEnabled && isAdmin ? () => openDrawerH('ops') : undefined}
          onSettings={openSettings}
          onMore={() => setKebabOpen(true)}
        />
      )}
      </div>{/* end chat-panel-inner */}
        </div>{/* end chat-panel */}

        {/* Panel 3 (browser iframe) disabled - see openBrowser comment above */}
      </div>{/* end view-slider */}
      {/* Modals rendered outside view-slider so position:fixed works correctly
          (view-panel has will-change:transform which creates a stacking context
          that breaks fixed positioning relative to viewport) */}
      <VoicePicker
        open={voicePickerOpen}
        voices={voices}
        selectedId={selectedVoiceId}
        onSelect={setVoice}
        onPreview={previewVoice}
        onClose={() => setVoicePickerOpen(false)}
      />
      <SshTargetPicker
        open={sshPickerOpen}
        targets={sshTargets}
        onSelect={sshConnect}
        onClose={() => setSshPickerOpen(false)}
      />
      {/* unified-drawer P3: DrawerHost — the ONE shared shell for every drawerStore panel
          (single owner, no per-drawer boolean). Mounted here (outside view-slider) so the
          sheet's position:fixed lands against the viewport. Panels are migrated onto it
          incrementally; usage is the first (r24). */}
      <DrawerHost
        onClose={closeDrawerH}
        panels={{
          // Agents-drawer Phase 1 (agents-drawer-carousel-reorg.md): the full
          // SessionListView slotted into the shared sheet, opened from the new leftmost
          // Agents bottom-nav button (openDrawerH('sessions')). The 'sessions' DrawerPanel
          // was reserved in drawerStore.ts and wired here for the first time — it reuses
          // the existing openDrawer trigger, so NO new TRANSITIONS row (SE-DR-11 stays
          // green; R-A1). drawerSessionListProps wraps every route-to-chat callback with
          // closeDrawer() (D2-a); onSettings/onFiles stay unwrapped → open→open swaps.
          // The .drawer-host-sessions wrapper gives SessionListView (.sl-view is
          // position:absolute; inset:0) a positioned, sized ancestor so it and its
          // absolutely-positioned FAB/scroll-spine are CONTAINED inside the 85svh sheet
          // (D2 containment) rather than collapsing to zero height.
          sessions: {
            title: '👥 Agents',
            enabled: true,
            render: () => (
              <div class="drawer-host-sessions">
                <SessionListView {...drawerSessionListProps} />
                {/* PWA install prompt — migrated to the coach substrate (Phase 3b, kind:'pill');
                    useChatShell mirrors installPromptAvailable/installDismissed/isStandaloneMode
                    into coach.show('pwa-install'); <CoachLayer/> renders it via action/onDismiss. */}
              </div>
            ),
          },
          usage: {
            title: '📊 Usage',
            enabled: usagePanelEnabled,
            render: () => <UsageTab isAdmin={isAdmin ?? false} />,
          },
          tasks: {
            title: '✅ Tasks',
            enabled: tasksTabEnabled,
            // Grafana render surface (ci-observability-grafana-dashboard.md §5.2): a
            // kiosk iframe of the CI Factory Floor dashboard for all observational
            // content, with a thin native strip keeping the mutating 20% — repo chips
            // that rewrite the iframe's var-repo AND "▶ Work on this" (closes the drawer
            // then prefills the composer via handleWorkOnItem). Panel id stays 'tasks'
            // (content swap, not a new panel — SE-AR-03 / R-A1). active: it's only
            // rendered when this panel is open, so it always fetches.
            render: () => (
              <GrafanaTasksPanel
                active
                onWorkOnItem={(wi) => { closeDrawer(); handleWorkOnItem(wi); }}
              />
            ),
          },
          // agent-runs §4: the Agent Runs sheet. Admin-gated on the SAME two conditions as
          // the cluster icon (and the backend re-checks both — the client gate is cosmetic).
          // Reuses the openDrawer trigger, so no new drawer TRANSITIONS row (R-A1).
          runs: {
            title: '🔀 Agent Runs',
            enabled: !!workflowVizEnabled && !!isAdmin,
            render: () => <AgentRunsPanel sessionId={currentSessionId} />,
          },
          ops: {
            title: '🛠️ Ops',
            enabled: !!opsPanelEnabled && !!isAdmin,
            // Same ops-proxy embed as the session-list Ops tab (admin-only). The
            // iframe gets an explicit height (.drawer-host-ops-iframe) since iframes
            // don't auto-size inside the flex body.
            render: () => (
              <iframe
                class="drawer-host-ops-iframe"
                src="/api/admin/ops-proxy"
                title="Ops Panel"
                allow="same-origin"
              />
            ),
          },
        }}
      />
      {/* Phase 4: removed autoSpeak/onToggleAutoSpeak/autoSubmitVoice/onToggleAutoSubmit/
          beetleVisible/onToggleBeetle props — SettingsModal subscribes to store directly. */}
      {drawerPanel === 'settings' && <SettingsModal onClose={closeSettings} memosEnabled={memosEnabled} billingEnabled={billingEnabled} readerModeEnabled={readerModeEnabled} logbookEnabled={logbookEnabled} browserPanelEnabled={browserPanelEnabled} sandboxFsEnabled={sandboxFsEnabled} onKeyChange={fetchSessionHealth} ttsSpeed={ttsSpeed} onCycleSpeed={cycleSpeed} />}
      {geoNeeded && <GeoGate onPassed={() => setGeoNeeded(false)} />}
      {!geoNeeded && consentNeeded && <ConsentModal onAccepted={() => setConsentNeeded(false)} />}
      {browserConsentPrompt && (
        <BrowserConsentModal
          version={browserConsentPrompt.version}
          onAccepted={() => {
            setBrowserConsentPrompt(null);
            coachShow({ id: 'browser-enabled', kind: 'toast', body: 'Browser enabled — re-send your message to use it.', ttlMs: 4000 });
          }}
          onDeclined={() => setBrowserConsentPrompt(null)}
        />
      )}
      {browserTierGatePrompt && (
        <BrowserTierGateModal onClose={() => setBrowserTierGatePrompt(null)} />
      )}
      {explainPayload && (
        <ExplainTargetModal
          payload={explainPayload}
          currentSessionId={currentSessionId}
          onHere={handleExplainHere}
          onSession={handleExplainToSession}
          onNewSession={handleExplainToNewSession}
          onClose={() => setExplainPayload(null)}
        />
      )}
      <ShareTargetModal
        open={shareTargetOpen}
        currentSessionId={currentSessionId}
        payload={pendingSharePayloadRef.current}
        onSendToSession={(sessionId) => { handleSendToSession(sessionId); exitSelectionMode(); }}
        onNewSession={() => { handleShareToNewSession(); exitSelectionMode(); }}
        onShareExternal={() => {
          const text = pendingShareTextRef.current ?? '';
          pendingShareTextRef.current = null;
          pendingShareTargetRef.current = null;
          pendingSharePayloadRef.current = null;
          doShareExternal(text);
          exitSelectionMode();
        }}
        onDownloadPdf={handleShareAsPdf}
        onDownloadFilePdf={handleShareFilePdf}
        onShareFileExternal={handleShareFileExternal}
        onClose={() => {
          setShareTargetOpen(false);
          if (!pendingShareTargetRef.current) {
            pendingShareTextRef.current = null;
            pendingSharePayloadRef.current = null;
          }
        }}
        interAgentSharingEnabled={interAgentSharingEnabled}
      />
      {/* Slash command popover — "Here" or "Elsewhere" */}
      {slashPopover && (
        <>
          <div class="slash-popover-overlay" onClick={() => setSlashPopover(null)} />
          <div
            class="slash-popover"
            style={{ left: `${Math.min(slashPopover.x, window.innerWidth - 180)}px`, top: `${slashPopover.y - 70}px` }}
          >
            <button
              class="slash-popover-btn slash-popover-here"
              onClick={() => { sendText(slashPopover.cmd); setSlashPopover(null); }}
            >Here</button>
            <button
              class="slash-popover-btn slash-popover-elsewhere"
              onClick={() => { doShare(slashPopover.cmd); setSlashPopover(null); }}
            >Elsewhere</button>
          </div>
        </>
      )}
      {/* In-session model picker sheet — gated OFF on every profile (#1071); session
          tuning (effort + 1M context) lives in the ContextBar popover. */}
      {modelSelectorEnabled && (
      <ModelControlSheet
        open={modelSheetOpen}
        currentModel={sessions.find((s) => s.id === currentSessionId)?.model ?? null}
        models={registryModels}
        geminiEnabled={geminiEnabled}
        onSwitchModel={(modelId) => {
          if (currentSessionId) {
            updateSessionTuning(currentSessionId, { model: modelId });
          }
        }}
        onNewChatWithModel={(modelId) => {
          setModelSheetOpen(false);
          handleNewSession(modelId);
        }}
        onConsultModel={(provider) => {
          setModelSheetOpen(false);
          // platform-isolation #1657 Phase 2b: write the consult-marker draft via the named
          // store action (was a `prefill-input` CustomEvent); focus via the view-layer event.
          if (currentSessionId) useComposerStore.getState().appendOrReplaceDraft(currentSessionId, `[CONSULT_MODEL: ${provider} | ]`, 'replace');
          document.dispatchEvent(new CustomEvent('focus-composer'));
        }}
        onClose={() => setModelSheetOpen(false)}
      />
      )}
      <HighCostEffortModal
        open={!!pendingHighCostSend}
        effort={pendingHighCostSend?.effort ?? 'high'}
        onConfirm={() => {
          const p = pendingHighCostSend;
          setPendingHighCostSend(null);
          p?.send();
        }}
        onCancel={() => setPendingHighCostSend(null)}
        onDropToHigh={currentSessionId ? () => {
          const p = pendingHighCostSend;
          setPendingHighCostSend(null);
          updateSessionTuning(currentSessionId, { effort: 'high' });
          p?.send();
        } : undefined}
      />
      {/* Onboarding wizard surfaces clone-repo / start-coding flows — voice-only brands skip it. */}
      {onboardingNeeded && !geoNeeded && !consentNeeded && !getBrand().voiceOnlyMode && (
        <OnboardingWizard
          provisioningStatus={userProvisioningStatus}
          onComplete={() => setOnboardingNeeded(false)}
          socket={socketRef.current}
          githubConnected={onboardingGithubConnected}
          githubLogin={onboardingGithubLogin}
          githubOauthEnabled={onboardingGithubOauthEnabled}
          brandId={getBrandId()}
          geminiEnabled={geminiEnabled}
        />
      )}
      <FileListDrawer
        open={drawerPanel === 'files'}
        title="Files"
        icon="📁"
        onClose={closeFiles}
        onConfigurePath={() => { closeFiles(); openSettings(); }}
        workspaceSections={{
          // Always-on with defaults — user override from settings if configured
          postsDirName: '_posts',
          opDocsDirName: tempDocsDirName || '_operational_docs',
          plansDirName: plansDirName || 'plans',
          projectDocsDirName: projectDocsDirName || 'docs',
          registryDirName: registryDirName || 'registry',
          uploadsDirName: 'uploads',
          // Archive is derived automatically from _archive/ subdirs within each section
        }}
      />
      <CodeViewer />

      {/* ── Reset session confirmation dialog ── */}
      {resetDialogOpen && (
        <div class="reset-dialog-overlay" onClick={() => setResetDialogOpen(false)}>
          <div class="reset-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 class="reset-dialog-title">Reset context?</h3>
            <p class="reset-dialog-subtitle">Older messages stay visible but won\u2019t be sent to the AI. The session and all history are kept.</p>
            <div class="reset-dialog-options">
              <label class="reset-dialog-checkbox">
                <input
                  type="checkbox"
                  checked={resetSaveKnowledge}
                  onChange={(e) => setResetSaveKnowledge((e.target as HTMLInputElement).checked)}
                />
                <div class="reset-dialog-option-text">
                  <span>Save knowledge first</span>
                  <span class="reset-dialog-hint">/handoff — snapshots this session, then briefs the next agent</span>
                </div>
              </label>
            </div>
            <div class="reset-dialog-actions">
              <button class="reset-dialog-btn reset-dialog-btn--cancel" onClick={() => setResetDialogOpen(false)}>Cancel</button>
              <button class="reset-dialog-btn reset-dialog-btn--confirm" onClick={confirmReset}>Reset context</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete session confirmation dialog ── */}
      {deleteDialogOpen && (
        <div class="reset-dialog-overlay" onClick={() => { setDeleteDialogOpen(false); setDeleteTargetIds([]); }}>
          <div class="reset-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 class="reset-dialog-title">
              {deleteTargetIds.length > 1 ? `Delete ${deleteTargetIds.length} sessions?` : 'Delete this session?'}
            </h3>
            <p class="reset-dialog-subtitle">This action cannot be undone.</p>
            <div class="reset-dialog-options">
              {deleteTargetIds.length > 1 ? (
                <div class="reset-dialog-bulk-warn">
                  <span class="reset-dialog-bulk-warn-icon">⚠</span>
                  <span>Knowledge will <strong>not</strong> be saved. All context from these {deleteTargetIds.length} sessions will be permanently lost.</span>
                </div>
              ) : (
                <label class="reset-dialog-checkbox">
                  <input
                    type="checkbox"
                    checked={deleteSaveKnowledge}
                    onChange={(e) => setDeleteSaveKnowledge((e.target as HTMLInputElement).checked)}
                  />
                  <div class="reset-dialog-option-text">
                    <span>Save knowledge</span>
                    <span class="reset-dialog-hint">/docs-update quick — fast snapshot of this session</span>
                  </div>
                </label>
              )}
            </div>
            <div class="reset-dialog-actions">
              <button class="reset-dialog-btn reset-dialog-btn--cancel" onClick={() => { setDeleteDialogOpen(false); setDeleteTargetIds([]); }}>No</button>
              <button class="reset-dialog-btn reset-dialog-btn--confirm reset-dialog-btn--danger" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {logbookOpen && (
        <LogbookModal
          onClose={closeLogbook}
          memosEnabled={memosEnabled}
          onExecute={(text) => { sendText(text); closeLogbook(); }}
          onSaveMemo={async (chunks, durationMs, language) => {
            const socket = socketRef.current;
            if (!socket) return;
            // Combine into one blob and send it base64-encoded as a STRING (not a binary
            // attachment) so socket.io emits a single plain EVENT packet, never a
            // BINARY_EVENT. This eliminates BOTH the binary-attachment reassembly desync
            // that corrupted prod audio (#1620 / #1622) AND the prior per-chunk async-emit
            // ordering race (the old forEach + arrayBuffer().then() could emit out of order,
            // which the 100ms setTimeout only papered over).
            const combined = new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' });
            const chunkB64 = await blobToBase64(combined);
            socket.emit(WsEvent.MEMO_AUDIO_CHUNK, { chunkB64 });
            socket.emit(WsEvent.MEMO_AUDIO_END, { durationMs, language: language || 'en' });
          }}
          onSaveTextMemo={async (text, images) => {
            const { authFetch } = await import('../api/authFetch');
            const res = await authFetch('/api/memos/from-text', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text, ...(images?.length ? { images } : {}) }),
            });
            if (!res.ok) throw new Error(await res.text());
          }}
          persistedText={logbookText}
          onTextChange={setLogbookText}
          persistedScreenshot={logbookScreenshot}
          onScreenshotChange={setLogbookScreenshot}
          onClearState={clearLogbookState}
        />
      )}
      <QuotePill sessionId={currentSessionId ?? null} />
      <ImageLightbox />
      <CoachLayer />
    </div>
  );
}
